import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarDays, MapPin, X, ArrowUpRight, Compass } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Footer from "@/components/Footer";
import { TRIPS } from "@/components/Travel";

export default function Expeditions() {
  const [selected, setSelected] = useState(null);
  const destinations = [...new Set(TRIPS.map((t) => t.country))];

  return (
    <div className="min-h-screen bg-[#FBF9F5]">
      <PageHeader />
      <main className="px-6 sm:px-12 lg:px-20 pt-32 sm:pt-40 pb-24 sm:pb-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl mb-12"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4E00] mb-5 flex items-center gap-3">
            <Compass className="w-4 h-4" /> Race Travel
          </p>
          <h1 data-testid="expeditions-headline" className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-[#0D1117] tracking-tight leading-[1.06]">
            Expeditions & <span className="italic text-[#FF4E00]">Destinations</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#374151] leading-relaxed">
            Bib, bed, transfers and tribe — curated journeys to the world's greatest start lines.
          </p>
        </motion.div>

        <div className="flex flex-wrap gap-2 mb-12" data-testid="destinations-strip">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B7280] py-2 pr-2">Destinations:</span>
          {destinations.map((d) => (
            <span key={d} className="font-mono text-[10px] uppercase tracking-[0.15em] border border-[#0D1117]/15 text-[#374151] rounded-full px-4 py-2">
              {d}
            </span>
          ))}
        </div>

        <h2 className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4E00] mb-8">Upcoming Expeditions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {TRIPS.map((t, i) => (
            <motion.button
              key={t.race}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: "easeOut" }}
              onClick={() => setSelected(t)}
              data-testid="expedition-card"
              className="group text-left rounded-3xl border border-[#0D1117]/10 hover:border-[#FF4E00]/40 bg-[#FFFFFF] overflow-hidden transition-all duration-300 hover:-translate-y-1"
            >
              <div className="relative aspect-[16/10] overflow-hidden">
                <img src={t.img} alt={t.race} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
                <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.15em] text-[#0D1117] bg-[#FBF9F5]/85 backdrop-blur px-3 py-1.5 rounded-full">
                  <MapPin className="w-3 h-3 text-[#FF4E00]" /> {t.city}, {t.country}
                </span>
              </div>
              <div className="p-6">
                <h3 className="font-serif text-xl text-[#0D1117] group-hover:text-[#FF4E00] transition-colors">{t.race}</h3>
                <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#6B7280] mt-2 flex items-center gap-2">
                  <CalendarDays className="w-3.5 h-3.5 text-[#FF4E00]" /> {t.dates}
                </p>
                <p className="font-mono text-xs text-[#0D1117] mt-2">
                  From <span className="text-[#FF4E00]">{t.price}</span> <span className="text-[#6B7280] text-[10px]">· per runner</span>
                </p>
              </div>
            </motion.button>
          ))}
        </div>
      </main>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-[#0D1117]/60 backdrop-blur-sm flex items-center justify-center p-6"
            onClick={() => setSelected(null)}
            data-testid="expedition-detail-modal"
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#FBF9F5] rounded-3xl max-w-xl w-full overflow-hidden relative"
            >
              <button onClick={() => setSelected(null)} className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-[#FBF9F5]/85 backdrop-blur flex items-center justify-center text-[#0D1117] hover:text-[#FF4E00]" aria-label="Close">
                <X className="w-4 h-4" />
              </button>
              <div className="aspect-[16/9]">
                <img src={selected.img} alt={selected.race} className="w-full h-full object-cover" />
              </div>
              <div className="p-7 sm:p-9">
                <h2 className="font-serif text-2xl sm:text-3xl text-[#0D1117]">{selected.race}</h2>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#FF4E00] mt-2.5">
                  {selected.city}, {selected.country} · {selected.dates}
                </p>
                <ul className="mt-5 space-y-2.5">
                  {selected.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2.5 text-sm text-[#374151]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#FF4E00] mt-1.5 shrink-0" />
                      {h}
                    </li>
                  ))}
                </ul>
                <div className="mt-7 flex flex-wrap items-center gap-4">
                  <a
                    href="/contact"
                    data-testid="expedition-enquire-button"
                    className="inline-flex items-center gap-2 bg-[#FF4E00] hover:bg-[#E04000] text-[#FBF9F5] font-mono text-xs uppercase tracking-[0.2em] px-7 py-3.5 rounded-full transition-colors duration-300"
                  >
                    Enquire — from {selected.price} <ArrowUpRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
